import React, { useEffect, useState } from 'react';
import { Box, Button, MenuItem, Select, Tab, Tabs, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';

import { updateGroupScheduleSetting } from '../../../api/branchScheduleSetting';
import { errorMessages } from '../../../utils/methods';
import { filterRoomsByLessonType, getRoomId } from '../../../constants/roomTypes';
import ClassroomManageModal from '../privateLesson/ClassroomManageModal';
import {
  CARD_MIN_OPTIONS,
  DEFAULT_CARD_MIN_MINUTES,
  SNAP_OPTIONS,
  TIME_UNIT_OPTIONS,
} from '../privateLesson/branchScheduleSetting';
import {
  FieldRow,
  HOURS,
  PRIMARY,
  PRIMARY_DARK,
  Segment,
  SettingDialog,
  SettingFields,
  SettingStatusBar,
  TIME_UNIT_LABEL,
  TabLabel,
  selectSx,
  tabsSx,
  toCardMinPercent,
  toSnapLabel,
  withTimeUnit,
} from '../privateLesson/BranchScheduleSettingModal';
import { groupScheduleSettingQueryKey } from './groupScheduleSetting';
import { WEEK_START_DAY_LABEL, WEEK_START_DAY_OPTIONS } from '../weekStart';

const TABS = [
  { value: 'time', label: '시간표' },
  { value: 'rooms', label: '강의실' },
];

// 저장 payload 모양 — 캘린더 시작·종료 null 은 지점 공용 시간 사용(아래 usesSharedCalendar 참고)
const toPayload = draft => ({
  timeUnitMinutes: draft.timeUnitMinutes,
  snapMinutes: draft.snapMinutes,
  cardMinMinutes: draft.cardMinMinutes,
  calendarStartTime: draft.calendarStartTime,
  calendarEndTime: draft.calendarEndTime,
  defaultRoomId: draft.defaultRoomId,
  weekStartDay: draft.weekStartDay,
});

// 그룹 전용 시간이 없으면(null) 입력 칸을 지금 그룹 화면이 쓰는 공용 시간으로 채운다(표시용).
// 사용자가 시각을 직접 바꾸지 않으면 저장 때 null 로 보내 공용을 유지한다
const toDraft = (setting, sharedTimes) => ({
  ...setting,
  calendarStartTime: setting.calendarStartTime ?? sharedTimes.startTime,
  calendarEndTime: setting.calendarStartTime != null ? setting.calendarEndTime : sharedTimes.endTime,
});

/**
 * 그룹 스케줄 설정 모달 — 시간표(캘린더 시간·표 칸·칸 선택 크기·카드 최소 높이) / 강의실(그룹 기본 강의실).
 * 지점 스케줄 설정 모달과 같은 모달·탭 구성이고, 바꾼 값은 저장 전에도 뒤 화면에 미리보기로 보인다.
 * 모든 항목이 그룹 설정 API 하나로 저장된다 — 설정을 못 받았으면(로딩·실패) 입력을 잠그고 저장하지 않는다.
 */
const GroupScheduleSettingModal = ({
  open,
  onClose,
  branchId,
  token,
  sharedTimes, // 지점 공용 캘린더 시간 { startTime, endTime } — 그룹 전용 시간이 없을 때 초기값
  setting,
  settingLoading = false,
  settingError = false,
  rooms = [],
  canManageRooms = false,
  onPreviewChange, // 저장 전 미리보기 설정 — 바뀐 게 없으면 null. 화면이 이 값으로 그린다
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const settingReady = !settingLoading && !settingError;
  const [draft, setDraftState] = useState(() => toDraft(setting, sharedTimes));
  const [dirty, setDirty] = useState(false);
  // 캘린더 시작·종료를 사용자가 직접 골랐는지 — 공용 시간을 쓰던 지점이 다른 항목만 바꿔 저장할 때 전용 값으로 굳지 않게
  const [timesEdited, setTimesEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roomManageOpen, setRoomManageOpen] = useState(false);
  const [tab, setTab] = useState('time');
  // 이번에 연 뒤 입력을 저장값으로 맞췄는지 — 맞추기 전(직전에 닫을 때 남은 입력)으로 미리보기를 내보내지 않는다
  const [synced, setSynced] = useState(false);

  const setDraft = updater => {
    if (!settingReady) return;
    setDirty(true);
    setDraftState(updater);
  };

  // 열 때마다 저장값으로 초기화
  useEffect(() => {
    if (!open) {
      setSynced(false);
      return;
    }
    setSynced(true);
    setTab('time');
    setDirty(false);
    setTimesEdited(false);
    setDraftState(toDraft(setting, sharedTimes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 연 뒤에 조회가 끝나면 아직 안 건드린 입력을 그 값으로 맞춘다
  useEffect(() => {
    if (!open || dirty) return;
    setDraftState(toDraft(setting, sharedTimes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setting, sharedTimes.startTime, sharedTimes.endTime]);

  const timeRangeInvalid = draft.calendarEndTime <= draft.calendarStartTime;
  const roomOptions = filterRoomsByLessonType(rooms, 'GROUP_LESSON', draft.defaultRoomId);
  // 원래 공용 시간(null)을 쓰고 있었고 캘린더 시각을 직접 바꾸지 않았으면 null 로 보내 공용을 유지한다.
  // (그사이 공용 시간이 늦게 받아져 화면 값이 달라져도 마찬가지 — 사용자가 고른 경우만 전용 값으로 저장)
  const usesSharedCalendar = setting.calendarStartTime == null;
  const sharedDraft = toDraft(setting, sharedTimes);
  const calendarChangedByUser =
    timesEdited &&
    (draft.calendarStartTime !== sharedDraft.calendarStartTime || draft.calendarEndTime !== sharedDraft.calendarEndTime);
  const payload = {
    ...toPayload(draft),
    ...(usesSharedCalendar && !calendarChangedByUser && { calendarStartTime: null, calendarEndTime: null }),
  };
  // 받아 온 값과 같으면 바뀐 게 없다 — 저장하지 않고, 닫을 때 묻지 않는다
  const changed = settingReady && JSON.stringify(payload) !== JSON.stringify(toPayload(setting));

  // 미리보기 — 바뀐 값을 저장 전에 화면에 보여 준다(서버 캐시는 건드리지 않는다). 시각 범위가 틀리면 시각은 저장값으로 둔다
  useEffect(() => {
    if (!open || !synced) return;
    onPreviewChange?.(
      changed
        ? {
            ...setting,
            ...payload,
            ...(timeRangeInvalid && {
              calendarStartTime: setting.calendarStartTime,
              calendarEndTime: setting.calendarEndTime,
            }),
          }
        : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, synced, changed, draft, timeRangeInvalid]);

  const handleSave = async () => {
    if (!settingReady) {
      onClose();
      return;
    }
    if (timeRangeInvalid) {
      setTab('time');
      toast.error('종료 시각은 시작 시각 이후여야 합니다');
      return;
    }
    if (!changed) {
      onClose();
      return;
    }
    // 기본 강의실이 지금 목록에 없으면(삭제 등) 비워서 보낸다. 강의실 목록을 아직 못 받았으면(로딩·실패로 빈 목록) 그대로 둔다
    const roomIds = new Set(rooms.map(getRoomId));
    const payloadToSend = { ...payload };
    if (rooms.length > 0 && payloadToSend.defaultRoomId != null && !roomIds.has(payloadToSend.defaultRoomId)) {
      payloadToSend.defaultRoomId = null;
    }

    setSaving(true);
    try {
      const saved = await updateGroupScheduleSetting(branchId, payloadToSend, token);
      queryClient.setQueryData(
        groupScheduleSettingQueryKey(branchId),
        saved && typeof saved === 'object' ? saved : payloadToSend,
      );
      toast.success('설정을 저장했습니다');
      onClose();
    } catch (error) {
      toast.error(`그룹 스케줄 설정 저장 실패: ${errorMessages(error)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SettingDialog
        open={open}
        title="그룹 스케줄 설정"
        changed={changed}
        saving={saving}
        onSave={handleSave}
        onClose={onClose}
        tabs={
          <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="fullWidth" sx={tabsSx}>
            {TABS.map(item => (
              <Tab
                key={item.value}
                value={item.value}
                label={<TabLabel label={item.label} hasError={item.value === 'time' && timeRangeInvalid} />}
              />
            ))}
          </Tabs>
        }
        status={
          !settingReady && (
            <SettingStatusBar error={settingError}>
              {settingError
                ? '그룹 스케줄 설정을 불러오지 못했습니다. 새로고침 후 다시 시도해 주세요.'
                : '그룹 스케줄 설정을 불러오는 중입니다. 불러온 뒤 수정할 수 있습니다.'}
            </SettingStatusBar>
          )
        }>
        {tab === 'time' && (
          <SettingFields disabled={!settingReady}>
            <FieldRow
              label="캘린더 시간"
              help={
                timeRangeInvalid ? (
                  <Box component="span" sx={{ color: '#E5484D' }}>
                    종료 시각은 시작 시각보다 늦어야 합니다.
                  </Box>
                ) : (
                  '그룹 스케줄 화면에만 적용됩니다.'
                )
              }>
              <Select
                size="small"
                sx={{ ...selectSx, width: '110px' }}
                value={draft.calendarStartTime}
                onChange={e => {
                  setTimesEdited(true);
                  setDraft(prev => ({ ...prev, calendarStartTime: e.target.value }));
                }}>
                {HOURS.map(hour => (
                  <MenuItem key={hour.value} value={hour.value}>
                    {hour.title}
                  </MenuItem>
                ))}
              </Select>
              <Typography sx={{ color: '#8A939C' }}>~</Typography>
              <Select
                size="small"
                error={timeRangeInvalid}
                sx={{ ...selectSx, width: '110px' }}
                value={draft.calendarEndTime}
                onChange={e => {
                  setTimesEdited(true);
                  setDraft(prev => ({ ...prev, calendarEndTime: e.target.value }));
                }}>
                {HOURS.map(hour => (
                  <MenuItem key={hour.value} value={hour.value} disabled={hour.value <= draft.calendarStartTime}>
                    {hour.title}
                  </MenuItem>
                ))}
              </Select>
            </FieldRow>
            <FieldRow label="표 칸 단위" help="시간표 한 칸(왼쪽 시간 줄)의 길이입니다.">
              <Segment
                options={TIME_UNIT_OPTIONS}
                value={draft.timeUnitMinutes}
                onChange={value => setDraft(withTimeUnit(value))}
                renderLabel={value => TIME_UNIT_LABEL[value]}
              />
            </FieldRow>
            <FieldRow label="칸 선택 크기" help="스케줄 표의 빈칸에 마우스를 올리거나 클릭할 때 선택되는 시간 간격입니다.">
              <Segment
                compact
                options={SNAP_OPTIONS}
                value={draft.snapMinutes}
                onChange={value => setDraft(prev => ({ ...prev, snapMinutes: value }))}
                isDisabled={value => value > draft.timeUnitMinutes}
                renderLabel={toSnapLabel}
              />
            </FieldRow>
            <FieldRow label="카드 최소 높이" help="1시간 칸 높이 대비 짧은 수업 카드의 최소 높이입니다.">
              <Select
                size="small"
                sx={{ ...selectSx, width: '150px' }}
                value={draft.cardMinMinutes}
                onChange={e => setDraft(prev => ({ ...prev, cardMinMinutes: e.target.value }))}>
                {CARD_MIN_OPTIONS.map(value => (
                  <MenuItem key={value} value={value}>
                    {value === DEFAULT_CARD_MIN_MINUTES ? `${toCardMinPercent(value)}% (기본)` : `${toCardMinPercent(value)}%`}
                  </MenuItem>
                ))}
              </Select>
            </FieldRow>
            <FieldRow label="주 시작 요일" help="그룹 주간 표의 첫 요일입니다.">
              <Segment
                compact
                options={WEEK_START_DAY_OPTIONS}
                value={draft.weekStartDay}
                onChange={value => setDraft(prev => ({ ...prev, weekStartDay: value }))}
                renderLabel={value => WEEK_START_DAY_LABEL[value]}
              />
            </FieldRow>
          </SettingFields>
        )}

        {tab === 'rooms' && (
          <SettingFields disabled={!settingReady}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '18px' }}>
              <Typography sx={{ flex: 1, fontSize: '13px', color: '#4D4D4D' }}>
                빈칸을 눌러 그룹수업을 만들 때 먼저 선택되는 강의실입니다.
              </Typography>
              {canManageRooms && (
                <Button
                  size="small"
                  onClick={() => setRoomManageOpen(true)}
                  sx={{
                    flexShrink: 0,
                    height: '32px',
                    px: '12px',
                    color: PRIMARY_DARK,
                    border: `1px solid ${PRIMARY}`,
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    fontWeight: 600,
                    '&:hover': { backgroundColor: '#F1FAFB' },
                  }}>
                  강의실 관리
                </Button>
              )}
            </Box>
            <FieldRow label="기본 강의실">
              <Select
                size="small"
                displayEmpty
                sx={{ ...selectSx, width: '100%', maxWidth: '320px' }}
                value={roomOptions.some(room => room.branchExerciseRoomId === draft.defaultRoomId) ? draft.defaultRoomId : ''}
                onChange={e =>
                  setDraft(prev => ({ ...prev, defaultRoomId: e.target.value === '' ? null : e.target.value }))
                }>
                <MenuItem value="">선택 안 함</MenuItem>
                {roomOptions.map(room => (
                  <MenuItem key={room.branchExerciseRoomId} value={room.branchExerciseRoomId}>
                    {room.branchExerciseRoomName}
                  </MenuItem>
                ))}
              </Select>
            </FieldRow>
          </SettingFields>
        )}
      </SettingDialog>

      {canManageRooms && (
        <ClassroomManageModal
          open={roomManageOpen}
          onClose={() => setRoomManageOpen(false)}
          branchId={branchId}
          rooms={rooms}
        />
      )}
    </>
  );
};

export default GroupScheduleSettingModal;
